import React, {useEffect, useState} from 'react';
import './AdminLoginPromoPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import RewardListEditor, {NUMERIC_REWARD_TYPES} from '../../elements/RewardListEditor/RewardListEditor';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {INVENTORY_ITEMS} from '../../../constants/inventoryItems';
import {getGameDayKey} from '../../../utils/date/getGameDayKey';

const PAGE_ID = 'AdminLoginPromoPage';

const EMPTY_REWARD = {itemType: INVENTORY_ITEMS.NUMENS, value: ''};

function formatDateTime(isoString) {
  if (!isoString) return '—';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })}, ${date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}`;
}

// Ключ игрового дня 'YYYY-MM-DD' -> «18 августа 2026 г.»
function formatDayKey(dayKey) {
  if (!dayKey) return '—';
  const date = new Date(`${dayKey}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return dayKey;
  return date.toLocaleDateString('ru-RU', {day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'});
}

/**
 * Админка, раздел «Акция за вход»: создание акции (тип пока всегда «За вход»,
 * название, награды, последний игровой день действия) и список всех акций —
 * действующих и завершенных. Награды настраиваются тем же RewardListEditor,
 * что и у приглашения друзей.
 *
 * Одновременно активной может быть только одна акция: сохранение новой
 * завершает предыдущую (это делает сервер, см. POST /api/admin/login-promos).
 */
function AdminLoginPromoPage({onBack}) {
  usePageAnalytics('AdminLoginPromoPage');

  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [name, setName] = useState('');
  const [rewards, setRewards] = useState([{...EMPTY_REWARD}]);
  const [activeUntilDayKey, setActiveUntilDayKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [savedMessage, setSavedMessage] = useState(null);

  const fetchPromos = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/login-promos`, {credentials: 'include'});
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Не удалось загрузить акции');
      }
      setPromos(data.promos || []);
    } catch (e) {
      console.error('Login promos fetch error', e);
      setLoadError(e.message || 'Не удалось загрузить акции');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromos();
  }, []);

  const handleSave = async () => {
    if (saving) return;
    setSavedMessage(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setFormError('Укажите название акции');
      return;
    }
    if (!activeUntilDayKey) {
      setFormError('Укажите дату окончания акции');
      return;
    }
    if (activeUntilDayKey < getGameDayKey(Date.now())) {
      setFormError('Дата окончания акции уже прошла');
      return;
    }

    // Пустые строки отбрасываются; частично заполненная — ошибка
    const filledRewards = rewards.filter((reward) => String(reward.value).trim());
    if (filledRewards.length === 0) {
      setFormError('Укажите хотя бы одну награду');
      return;
    }
    for (const reward of filledRewards) {
      if (NUMERIC_REWARD_TYPES.includes(reward.itemType)) {
        const amount = parseInt(reward.value, 10);
        if (!Number.isInteger(amount) || amount <= 0) {
          setFormError('Количество награды должно быть целым положительным числом');
          return;
        }
      }
    }
    setFormError(null);

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/admin/login-promos`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          name: trimmedName,
          activeUntilDayKey,
          rewards: filledRewards.map((reward) => ({
            itemType: reward.itemType,
            value: String(reward.value).trim(),
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Не удалось сохранить акцию');
      }
      setName('');
      setRewards([{...EMPTY_REWARD}]);
      setActiveUntilDayKey('');
      setSavedMessage('Акция сохранена');
      // Перечитываем список целиком: сохранение могло завершить предыдущую
      // активную акцию, и ее карточка тоже должна обновиться
      await fetchPromos();
    } catch (e) {
      console.error('Save login promo error', e);
      setFormError(e.message || 'Не удалось сохранить акцию');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h1 className="lobby-page-title">Акция за вход</h1>

      <div className="admin-login-promo-form">
        <label className="admin-login-promo-label" htmlFor="admin-login-promo-type">Тип акции</label>
        <input
          id="admin-login-promo-type"
          className="admin-login-promo-input"
          type="text"
          value="За вход"
          readOnly
          disabled
        />

        <label className="admin-login-promo-label" htmlFor="admin-login-promo-name">Название акции</label>
        <input
          id="admin-login-promo-name"
          className="admin-login-promo-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Например: Запуск обновления"
          disabled={saving}
        />

        <span className="admin-login-promo-label">Награды акции</span>
        <RewardListEditor rewards={rewards} onChange={setRewards} disabled={saving}/>

        <label className="admin-login-promo-label" htmlFor="admin-login-promo-until">
          Действует до конца дня (включительно)
        </label>
        <input
          id="admin-login-promo-until"
          className="admin-login-promo-input"
          type="date"
          value={activeUntilDayKey}
          onChange={(e) => setActiveUntilDayKey(e.target.value)}
          disabled={saving}
        />

        {formError && <p className="admin-login-promo-status admin-login-promo-status_error">{formError}</p>}
        {savedMessage && <p className="admin-login-promo-status admin-login-promo-status_success">{savedMessage}</p>}

        <div className="main-menu-buttons admin-login-promo-save-button">
          <PageButton onClick={handleSave} pageId={PAGE_ID} buttonId="save" disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </PageButton>
        </div>
      </div>

      <h2 className="admin-login-promo-section-title">Акции</h2>
      {loading && <p className="admin-login-promo-status">Загрузка...</p>}
      {loadError && <p className="admin-login-promo-status admin-login-promo-status_error">{loadError}</p>}
      {!loading && !loadError && (
        promos.length === 0 ? (
          <p className="admin-login-promo-status">Акций пока нет</p>
        ) : (
          <div className="admin-login-promo-list">
            {promos.map((promo) => (
              <div key={promo.promoId} className="admin-login-promo-card">
                <div className="admin-login-promo-card-header">
                  <span className="admin-login-promo-card-name">{promo.name}</span>
                  <span
                    className={`admin-login-promo-card-state ${
                      promo.isActive ? 'text-success' : 'admin-login-promo-card-state_finished'
                    }`}
                  >
                    {promo.isActive ? 'Действует' : 'Завершена'}
                  </span>
                </div>

                <div className="admin-login-promo-card-row">
                  <span className="admin-login-promo-card-label">Тип</span>
                  <span>За вход</span>
                </div>
                <div className="admin-login-promo-card-row">
                  <span className="admin-login-promo-card-label">Начало</span>
                  <span>{formatDateTime(promo.startedAt)}</span>
                </div>
                <div className="admin-login-promo-card-row">
                  <span className="admin-login-promo-card-label">Действует до</span>
                  <span>{formatDayKey(promo.activeUntilDayKey)}</span>
                </div>
                <div className="admin-login-promo-card-row">
                  <span className="admin-login-promo-card-label">Приняли участие</span>
                  <span className="text-coins">{promo.claimedCount ?? 0}</span>
                </div>
                {promo.endedAt && (
                  <div className="admin-login-promo-card-row">
                    <span className="admin-login-promo-card-label">Завершена досрочно</span>
                    <span>{formatDateTime(promo.endedAt)}</span>
                  </div>
                )}

                <span className="admin-login-promo-card-label">Награды</span>
                <RewardListEditor
                  rewards={promo.rewards} onChange={() => {
                }} readOnly
                />
              </div>
            ))}
          </div>
        )
      )}

      <div className="main-menu-buttons admin-login-promo-buttons">
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>
    </>
  );
}

export default AdminLoginPromoPage;
